import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Heart, Loader, MapPin, Search } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import authAxios from "../../authAxios";
import instance from "../../axios";
import Toast from "../../components/common/Toast";
import { useGlobalState } from "../../GlobalStates";
import { Footer, StickyHeader } from "../../intro/components/landing-new";
import { uploadBigFileToS3 } from "../../services/uploads";
import GuideDetailModal from "./components/GuideDetailModal";
import GuideFormModal from "./components/GuideFormModal";

const BringYourGuides = () => {
  const [guides, setGuides] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [toast, setToast] = useState(null);
  const [votedGuides, setVotedGuides] = useState(new Set());
  const [newGuide, setNewGuide] = useState({
    guide_name: "",
    description: "",
    contact_info: "",
    trip_experience: "",
    images: [],
    imagePreviews: [],
  });

  const [user] = useGlobalState("user");

  const fetchGuides = useCallback(async () => {
    try {
      const response = await instance.get("/v1/bringyourguides", {
        params: {
          userId: user?.id,
        },
      });
      setGuides(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get("success");
    const search = urlParams.get("search");

    if (success === "true") {
      showToast(
        "We've listed your guide nomination! Share this page so others can vote for them too! 🎉",
        "celebration"
      );
    }

    if (search) {
      setSearchQuery(search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchGuides();
  }, [fetchGuides]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewGuide((prev) => ({
          ...prev,
          images: [...prev.images, file],
          imagePreviews: [...prev.imagePreviews, reader.result],
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (index) => {
    setNewGuide((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
      imagePreviews: prev.imagePreviews.filter((_, i) => i !== index),
    }));
  };

  const getVotedGuides = async () => {
    try {
      const response = await authAxios.get("/v1/bringyourguides/getVotedGuides");
      setVotedGuides(new Set(response.data.map((guide) => guide.bringyourguides_id)));
    } catch (error) {
      console.error("Error fetching voted guides data:", error);
    }
  };

  useEffect(() => {
    if (user?.id) {
      getVotedGuides();
    }
  }, [user?.id]);

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const handleVote = async (id) => {
    if (votedGuides.has(id)) {
      return;
    }

    try {
      setVotedGuides((prev) => new Set([...prev, id]));
      await authAxios.post(`/v1/bringyourguides/${id}/vote`, {
        increment: true,
        userId: user.id,
      });
      await fetchGuides();
      showToast("Thank you for voting! 🙏", "success");
    } catch (error) {
      showToast(error.response?.data?.error || "Voting failed. Please try again.", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);

    if (newGuide.images.length === 0) {
      showToast("Please upload at least one photo of your guide", "error");
      setLoadingSubmit(false);
      return;
    }

    // Validate required fields
    if (!newGuide.guide_name?.trim()) {
      showToast("Please enter your guide's name", "error");
      setLoadingSubmit(false);
      return;
    }

    if (!newGuide.description?.trim()) {
      showToast("Please tell us why this guide should come!", "error");
      setLoadingSubmit(false);
      return;
    }

    if (!newGuide.trip_experience?.trim()) {
      showToast("Please tell us which trip you met this guide on", "error");
      setLoadingSubmit(false);
      return;
    }

    try {
      // Upload all images first
      const uploadedImageUrls = await Promise.all(
        newGuide.images.map((image) => uploadBigFileToS3(image))
      );

      const guideData = {
        guide_name: newGuide.guide_name,
        description: newGuide.description,
        contact_info: newGuide.contact_info,
        trip_experience: newGuide.trip_experience,
        images: uploadedImageUrls,
      };

      if (user?.id) {
        guideData.userId = user.id;
      }

      await authAxios.post("/v1/bringyourguides", guideData);

      setShowForm(false);
      setNewGuide({
        guide_name: "",
        description: "",
        contact_info: "",
        trip_experience: "",
        images: [],
        imagePreviews: [],
      });

      showToast(
        "Thank you for nominating this guide! Share this page so others can vote for them! 🎉",
        "celebration"
      );

      await fetchGuides();
    } catch (error) {
      console.error("Error submitting guide nomination:", error);
      showToast("Failed to nominate guide. Error: " + error.message, "error");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const filteredGuides = guides
    .filter(
      (guide) =>
        guide.guide_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        guide.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        guide.trip_experience?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => b.votes - a.votes);

  return (
    <div className="tw-min-h-screen tw-bg-gradient-to-br tw-from-sky-50 tw-via-white tw-to-blue-50 tw-px-4 tw-pb-10 tw-pt-24 sm:tw-px-6 lg:tw-px-8">
      <StickyHeader withoutCta />
      <div className="tw-mx-auto tw-min-h-[100vh] tw-max-w-4xl">
        <div className="tw-text-center">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="tw-mb-4 tw-bg-gradient-to-r tw-from-[#ff69b4] tw-to-[#ff4136] tw-bg-clip-text tw-text-4xl tw-font-bold tw-leading-tight tw-text-transparent"
          >
            Fly your guide to SOFEST!
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="tw-mb-8 tw-text-lg tw-text-gray-600"
          >
            We want to bring our guides around this time! Help us choose who should come!
          </motion.p>

          <div className="tw-relative tw-mx-auto tw-mb-8 tw-w-full tw-max-w-4xl tw-rounded-lg">
            <img
              src="/images/bring-your-guide.jpg"
              alt="Bring Your Guides"
              className="tw-object-cover tw-shadow-lg md:tw-h-[500px]"
              style={{ width: "100%", height: "auto", borderRadius: "20px" }}
            />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="tw-mx-auto tw-mb-12 tw-max-w-2xl tw-text-center"
          >
            <h2 className="tw-mb-4 tw-text-2xl tw-font-bold tw-text-gray-900">
              Nominate & vote for your favourite guides
            </h2>
            <p className="tw-leading-relaxed tw-text-gray-600">
              We always travel to meet our guides, but what if we brought them to meet us? Nominate
              guides who've made an impact on your trips, and vote for your favorites to join us at
              Sofest! 🌟
            </p>
          </motion.div>

          <div className="tw-mb-8 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4">
            <button
              onClick={() => {
                if (!localStorage.getItem("USER_ID")) {
                  window.location.href = `${window.location.origin}/login?reroute=/bring-your-guides`;
                  return;
                }
                setShowForm(!showForm);
              }}
              className="tw-rounded-full tw-bg-gradient-to-r tw-from-[#ff69b4] tw-to-[#ff4136] tw-px-8 tw-py-4 tw-font-semibold tw-text-white tw-shadow-lg tw-transition-all hover:tw-shadow-xl"
            >
              Nominate a Guide
            </button>
          </div>

          <div className="tw-mb-7 tw-mt-14 tw-flex tw-flex-wrap tw-justify-center tw-gap-2">
            <div className="tw-relative tw-w-full sm:tw-w-96">
              <Search className="tw-absolute tw-left-3 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-gray-400" />
              <input
                type="text"
                placeholder="Search for guides..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="tw-w-full tw-rounded-full tw-border-0 tw-bg-white tw-py-2 tw-pl-10 tw-pr-4 tw-text-sm tw-shadow tw-transition-all focus:tw-ring-2 focus:tw-ring-[deepskyblue]"
              />
            </div>
          </div>
        </div>

        <GuideFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          newGuide={newGuide}
          setNewGuide={setNewGuide}
          handleSubmit={handleSubmit}
          loadingSubmit={loadingSubmit}
          handleImageChange={handleImageChange}
          handleRemoveImage={handleRemoveImage}
        />

        <GuideDetailModal
          open={!!selectedGuide}
          selectedGuide={selectedGuide}
          onClose={() => setSelectedGuide(null)}
          handleVote={handleVote}
          votedGuides={votedGuides}
          showToast={showToast}
        />

        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}

        <div className="tw-space-y-4">
          {isLoading ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12">
              <motion.div
                animate={{
                  rotate: 360,
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Loader className="tw-h-8 tw-w-8 tw-text-[deepskyblue]" />
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="tw-mt-4 tw-text-gray-500"
              >
                Loading guide nominations...
              </motion.p>
            </div>
          ) : filteredGuides.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="tw-py-12 tw-text-center"
            >
              <motion.div
                animate={{
                  y: [0, -10, 0],
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <MapPin className="tw-mx-auto tw-mb-4 tw-h-16 tw-w-16 tw-text-gray-300" />
              </motion.div>
              <motion.h3
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="tw-mb-2 tw-text-xl tw-font-semibold tw-text-gray-900"
              >
                No guides found
              </motion.h3>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="tw-text-gray-500"
              >
                Be the first to nominate an amazing guide!
              </motion.p>
            </motion.div>
          ) : (
            <AnimatePresence mode="popLayout" initial={false}>
              {filteredGuides.map((guide, index) => (
                <motion.div
                  layout="position"
                  key={guide.id}
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -20 }}
                  transition={{
                    opacity: { duration: 0.2 },
                    scale: { duration: 0.3 },
                    layout: { duration: 0.3 },
                    delay: index * 0.1,
                  }}
                  className="tw-overflow-hidden tw-rounded-xl tw-bg-white tw-shadow-sm tw-transition-shadow hover:tw-shadow-md"
                >
                  <div className="tw-flex tw-items-center tw-justify-between tw-p-4">
                    <div
                      className="tw-flex tw-items-center"
                      onClick={() => setSelectedGuide(guide)}
                    >
                      <div className="tw-mr-3 tw-h-16 tw-w-16 tw-flex-shrink-0 tw-overflow-hidden tw-rounded-full">
                        <img
                          src={guide.images?.[0] || "/placeholder.jpg"}
                          alt=""
                          className="tw-h-full tw-w-full tw-rounded-full tw-object-cover"
                        />
                      </div>
                      <div className="tw-flex tw-flex-col">
                        <span className="tw-text-lg tw-font-medium">{guide.guide_name}</span>
                        <span className="tw-text-sm tw-text-gray-500">
                          Nominated by {guide.author || "Anonymous"}
                        </span>
                        {guide.trip_experience && (
                          <span className="tw-text-xs tw-text-gray-400">
                            Trip: {guide.trip_experience}
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(
                          `${window.location.origin}/bring-your-guides?search=${guide.guide_name}`
                        );
                        showToast("Link copied to clipboard!", "success");
                      }}
                      className="tw-flex tw-items-center tw-gap-1 tw-p-2 tw-text-gray-500 hover:tw-text-gray-700"
                    >
                      <span>Share</span>
                      <ArrowUpRight className="tw-h-5 tw-w-5" />
                    </button>
                  </div>

                  <div className="tw-p-3">
                    <p
                      className="tw-mb-2 tw-line-clamp-2 tw-text-gray-600"
                      onClick={() => setSelectedGuide(guide)}
                    >
                      {guide.description}
                    </p>
                    <div className="tw-my-4 tw-flex tw-items-center tw-justify-between">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        className="tw-flex tw-items-center tw-space-x-1"
                        onClick={() => handleVote(guide.id)}
                      >
                        <Heart
                          className={`tw-h-6 tw-w-6 ${
                            votedGuides.has(guide.id)
                              ? "tw-fill-rose-500 tw-text-rose-500"
                              : "tw-text-rose-500"
                          }`}
                        />
                        <span className="tw-font-medium">{guide.votes} votes</span>
                      </motion.button>

                      <button
                        className="tw-font-medium tw-text-pink-500 hover:tw-text-blue-700"
                        onClick={() => setSelectedGuide(guide)}
                      >
                        View More
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BringYourGuides;
